import i18next from 'i18next';
import React from 'react';
import { ButtonBase, Icon, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import classNames from 'classnames';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  button: {
    display: 'inline-block',
    cursor: 'pointer',
    border: 'none',
    padding: 0,
    background: 'none',
    whiteSpace: 'nowrap',
  },
  text: {
    verticalAlign: 'middle',
    fontSize: '14px',
    lineHeight: '16px',
    color: theme.palette.text.primary,
  },
  'text-non_compliant': {
    color: theme.palette.text.grey,
  },
  icon: {
    fontSize: '17px',
    marginRight: '1vw',
    verticalAlign: 'middle',
  },
  'icon-compliant': {
    color: '#2e7d32',
  },
  'icon-partial': {
    color: '#ed6c02',
  },
  'icon-non_compliant': {
    color: theme.palette.text.grey,
  },
  inlineLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
  },
  inlineHint: {
    fontSize: '12px',
    lineHeight: '14px',
    color: theme.palette.text.grey,
    display: 'inline-flex',
    alignItems: 'center',
    marginLeft: '8px',
    padding: '0 4px',
  },
  eligibleCount: {
    color: '#2e7d32',
    fontWeight: 600,
  },
  notEligibleCount: {
    color: theme.palette.error.main,
    fontWeight: 600,
  },
  totalCount: {
    color: theme.palette.text.grey,
    fontWeight: 500,
  },
  separator: {
    color: theme.palette.text.grey,
    margin: '0 4px',
  },
}));

const ComplianceStatusCell = ({ compliance, id, onChange }) => {
  const classes = useStyles();
  const status = compliance?.status || 'non_compliant';
  const compliantRolesCount = compliance?.compliantRolesCount ?? 0;
  const nonCompliantRolesCount = compliance?.nonCompliantRolesCount ?? 0;
  const totalRolesCount = compliance?.totalRolesCount ?? 0;
  const statusLabel = i18next.t(`compliance.studentStatuses.${status}`, { defaultValue: status });

  return (
    <div className={classes.root}>
      <ButtonBase className={classes.button} data-key={id} aria-label="compliance" onClick={onChange}>
        <Icon className={classNames(classes.icon, classes[`icon-${status}`])}>lens</Icon>
        <Typography className={classNames(classes.text, classes[`text-${status}`])} component="span" variant="body1">
          <span className={classes.inlineLabel}>
            <span>{statusLabel}</span>
            <span className={classes.inlineHint}>
              <span className={classes.eligibleCount}>{compliantRolesCount}</span>
              <span className={classes.separator}>/</span>
              <span className={classes.notEligibleCount}>{nonCompliantRolesCount}</span>
              <span className={classes.separator}>/</span>
              <span className={classes.totalCount}>{totalRolesCount}</span>
            </span>
          </span>
        </Typography>
      </ButtonBase>
    </div>
  );
};

const renderJobTasksWithHighlights = (jobTasks, meta) => {
  if (typeof jobTasks !== 'string' || !jobTasks.trim()) {
    return jobTasks;
  }

  const notEligibleTasks = Array.isArray(meta?.notEligibleJobTasks)
    ? meta.notEligibleJobTasks.map(item => String(item).trim()).filter(Boolean)
    : [];
  const notEligibleSet = new Set(notEligibleTasks);
  const roles = jobTasks
    .split(',')
    .map(item => item.trim())
    .filter(Boolean);

  return (
    <span>
      {roles.map((role, index) => {
        const isNotEligible = notEligibleSet.has(role);
        return (
          <span
            key={`${role}-${index}`}
            style={{
              color: isNotEligible ? '#d32f2f' : '#2e7d32',
              fontWeight: isNotEligible ? 600 : 500,
            }}
          >
            {role}
            {index < roles.length - 1 ? ', ' : ''}
          </span>
        );
      })}
    </span>
  );
};

const getTableConfig = helpers => [
  {
    title: i18next.t('tablesLabels.studentId'),
    field: 'ids',
  },
  {
    title: i18next.t('tablesLabels.name'),
    field: 'fullName',
    isSorting: true,
  },
  {
    title: i18next.t('tablesLabels.email'),
    field: 'email',
    isSorting: true,
  },
  {
    title: i18next.t('tablesLabels.station'),
    field: 'stations',
    isSorting: true,
  },
  {
    title: i18next.t('tablesLabels.jobsTasks'),
    field: 'jobTasks',
    dependentField: 'jobTasksHighlightMeta',
    Component: (jobTasks, meta) => renderJobTasksWithHighlights(jobTasks, meta),
    isSorting: true,
  },
  {
    title: i18next.t('complianceStatus'),
    field: 'compliance',
    dependentField: 'id',
    Component: (compliance, id) => (
      <ComplianceStatusCell compliance={compliance} id={id} onChange={helpers.handleOpenComplianceModal} />
    ),
  },
  {
    title: '',
    field: 'id',
    isVisible: false,
  },
];

export default getTableConfig;
